import { useMemo, useState } from "react"
import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useReferenceState } from "lib/@hooks/use-reference"
import { setFromEvent } from "lib/setFromEvent"
import { NoResults } from "lib/@components/no-results/no-results"
import { confirm } from "lib/@dialogs/confirm"
import { SortableTableCell } from "routes/schedule/plugins/legislation-resolutions/sortable-table-cell"
import {
    ResolutionListFilterDropdowns,
    useResolutionFilters,
    applyResolutionFilters,
} from "routes/schedule/plugins/legislation-resolutions/resolution-list-filters"
import { criticalityRank } from "routes/schedule/plugins/legislation-resolutions/criticality-config"
import { getCopResolutionsByGroup } from "./controller/get-cop-resolutions-by-group.controller"
import { clearCopResolutions } from "./controller/clear-cop-resolutions.controller"
import { CopResolutionListRow } from "./cop-resolution-list-row"

export function CopResolutionList({ codeOfPracticeId }) {
    const resolutions = getCopResolutionsByGroup.useResults({ codeOfPracticeId }, withInitialValue(IS_LOADING))
    const [search, setSearch] = useState("")
    const searchLower = search.toLowerCase()
    const filters = useResolutionFilters()
    const [sortField, setSortField] = useReferenceState("cop-resolution-sort-field", "code")
    const [sortDirection, setSortDirection] = useReferenceState("cop-resolution-sort-dir", "asc")

    const filtered =
        resolutions && resolutions !== IS_LOADING
            ? applySearch(applyResolutionFilters(resolutions, filters), searchLower)
            : []
    const sorted = useMemo(applySorting, [filtered, sortField, sortDirection])

    if (resolutions === IS_LOADING) return <LoadingScreen />
    if (!resolutions) return <NoResults message="No scan results yet. Click 'Scan All Schedules' to start." />

    const counts = summariseCounts(resolutions)

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{ px: 1, pt: 1, pb: 0.5 }}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
            >
                <SummaryChip label="Inconsistencies" count={counts.total} />
                <SummaryChip label="Pending" count={counts.pending} color="default" />
                <If value={counts.processing}>
                    <SummaryChip label="Processing" count={counts.processing} color="info" />
                </If>
                <SummaryChip label="Resolved" count={counts.complete} color="success" />
                <If value={counts.error}>
                    <SummaryChip label="Errors" count={counts.error} color="error" />
                </If>
                <If value={counts.applied}>
                    <SummaryChip label="Applied" count={counts.applied} color="primary" />
                </If>
                <Box sx={{ ml: "auto" }}>
                    <Button
                        size="small"
                        color="secondary"
                        startIcon={<Iconify icon="mdi:delete-sweep-outline" />}
                        onClick={handleClearAll}
                    >
                        Clear All Resolutions
                    </Button>
                </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ px: 1, pb: 1 }}>
                <ResolutionListFilterDropdowns />
            </Stack>

            <Box sx={{ px: 1, pb: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    placeholder="Search by code or title…"
                    value={search}
                    onChange={setFromEvent(setSearch)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton aria-label="Clear search" size="small" onClick={clearSearch}>
                                    <Iconify icon="ic:round-clear" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box flex={1} sx={{ overflowY: "auto" }}>
                <If truthy={sorted.length}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40, pr: 0 }} />
                                <SortableTableCell
                                    field="code"
                                    label="Code"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableTableCell
                                    field="title"
                                    label="Schedule"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Status</TableCell>
                                <TableCell>Types</TableCell>
                                <SortableTableCell
                                    field="criticality"
                                    label="Criticality"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Changes</TableCell>
                                <TableCell>Decisions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat list={sorted} item={<CopResolutionListRow />} keyFn={keyById} />
                        </TableBody>
                    </Table>
                    <Else>
                        <NoResults message="No records match the current filters." />
                    </Else>
                </If>
            </Box>
        </Box>
    )

    async function handleClearAll() {
        const ok = await confirm(
            `This will permanently remove all ${resolutions.length} inconsistencies and their resolutions for this group. The scan counters will be reset so you can run a fresh scan.\n\nThis cannot be undone.`,
            "Clear All Resolutions"
        )
        if (!ok) return
        await clearCopResolutions({ codeOfPracticeId })
    }

    function clearSearch() {
        setSearch("")
    }

    function handleSort(field) {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc")
        } else {
            setSortField(field)
            setSortDirection(field === "criticality" ? "desc" : "asc")
        }
    }

    function applySorting() {
        if (!filtered.length) return filtered
        const dir = sortDirection === "asc" ? 1 : -1
        return [...filtered].sort((a, b) => {
            if (sortField === "criticality") {
                return (criticalityRank(a.criticality) - criticalityRank(b.criticality)) * dir
            }
            const av = (a[sortField] ?? "").toLowerCase()
            const bv = (b[sortField] ?? "").toLowerCase()
            return av.localeCompare(bv) * dir
        })
    }
}

function SummaryChip({ label, count, color = "default" }) {
    return <Chip label={`${label}: ${count}`} size="small" color={color} variant="outlined" />
}

function keyById(item) {
    return item._id
}

function summariseCounts(resolutions) {
    const counts = { total: resolutions.length, pending: 0, processing: 0, complete: 0, error: 0, applied: 0 }
    for (const r of resolutions) {
        const status = r.status ?? "pending"
        if (status in counts) counts[status]++
        else counts.pending++
    }
    return counts
}

function applySearch(items, search) {
    if (!search) return items
    return items.filter(
        (item) => item.code?.toLowerCase().startsWith(search) || item.title?.toLowerCase().includes(search)
    )
}
